import { apiClient } from './apiClient';

export type CustomerSuccessAiSuggestFieldSettings = {
  enabled: boolean;
  systemPrompt: string;
  userInstruction: string;
  temperature?: number | null;
  maxOutputTokens?: number | null;
};

export type CustomerSuccessAiSuggestSettingsView = {
  hasApiKey: boolean;
  endpoint: string;
  chatModel: string;
  defaultTemperature: number;
  includeCannedInAiSuggest: boolean;
  archiveReason: CustomerSuccessAiSuggestFieldSettings;
  reopenReason: CustomerSuccessAiSuggestFieldSettings;
  reply: CustomerSuccessAiSuggestFieldSettings;
  internalNote: CustomerSuccessAiSuggestFieldSettings;
  broadcast: CustomerSuccessAiSuggestFieldSettings;
};

export type CustomerSuccessGeneralSettings = {
  publicAppBaseUrl: string;
  welcomeEnabled: boolean;
  welcomeEmailTemplateId: string | null;
  csatEnabled: boolean;
  csatSurveyFormId: string | null;
  npsEnabled: boolean;
  npsSurveyFormId: string | null;
  npsFrequency: 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
  csatIncludeTicketContact: boolean;
  csatCommunicationRoleId: string | null;
  npsCommunicationRoleId: string | null;
  welcomeCommunicationRoleId: string | null;
};

export type CustomerSuccessAttachmentSettings = {
  maxAttachments: number;
  maxAttachmentBytes: number;
  allowedExtensions: string[];
};

export type CustomerSuccessModuleSettings = {
  general: CustomerSuccessGeneralSettings;
  attachments: CustomerSuccessAttachmentSettings;
  aiSuggest: CustomerSuccessAiSuggestSettingsView;
};

export type CustomerSuccessAiSuggestSettingsUpsert = {
  apiKey?: string | null;
  endpoint?: string | null;
  chatModel?: string | null;
  defaultTemperature?: number | null;
  includeCannedInAiSuggest?: boolean | null;
  archiveReason?: CustomerSuccessAiSuggestFieldSettings | null;
  reopenReason?: CustomerSuccessAiSuggestFieldSettings | null;
  reply?: CustomerSuccessAiSuggestFieldSettings | null;
  internalNote?: CustomerSuccessAiSuggestFieldSettings | null;
  broadcast?: CustomerSuccessAiSuggestFieldSettings | null;
};

const DEFAULT_ATTACHMENTS: CustomerSuccessAttachmentSettings = {
  maxAttachments: 10,
  maxAttachmentBytes: 52_428_800,
  allowedExtensions: [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.md',
    '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg',
    '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm', '.mp4',
    '.zip', '.rar', '.7z',
  ],
};

const DEFAULT_GENERAL: CustomerSuccessGeneralSettings = {
  publicAppBaseUrl: '',
  welcomeEnabled: true,
  welcomeEmailTemplateId: null,
  csatEnabled: true,
  csatSurveyFormId: null,
  npsEnabled: true,
  npsSurveyFormId: null,
  npsFrequency: 'Monthly',
  csatIncludeTicketContact: true,
  csatCommunicationRoleId: null,
  npsCommunicationRoleId: null,
  welcomeCommunicationRoleId: null,
};

function normalizeModuleSettings(data: CustomerSuccessModuleSettings): CustomerSuccessModuleSettings {
  return {
    general: {
      ...DEFAULT_GENERAL,
      ...(data.general ?? {}),
      welcomeEmailTemplateId: data.general?.welcomeEmailTemplateId ?? null,
      csatSurveyFormId: data.general?.csatSurveyFormId ?? null,
      npsSurveyFormId: data.general?.npsSurveyFormId ?? null,
      npsFrequency: data.general?.npsFrequency ?? 'Monthly',
      csatIncludeTicketContact: data.general?.csatIncludeTicketContact ?? true,
      csatCommunicationRoleId: data.general?.csatCommunicationRoleId ?? null,
      npsCommunicationRoleId: data.general?.npsCommunicationRoleId ?? null,
      welcomeCommunicationRoleId: data.general?.welcomeCommunicationRoleId ?? null,
    },
    attachments: {
      ...DEFAULT_ATTACHMENTS,
      ...(data.attachments ?? {}),
      allowedExtensions:
        data.attachments?.allowedExtensions?.length
          ? data.attachments.allowedExtensions
          : DEFAULT_ATTACHMENTS.allowedExtensions,
    },
    aiSuggest: data.aiSuggest,
  };
}

export async function getCustomerSuccessSettings(): Promise<CustomerSuccessModuleSettings> {
  const response = await apiClient.get<CustomerSuccessModuleSettings>(
    '/v1/customer-success/settings',
  );
  return normalizeModuleSettings(response.data);
}

export async function saveCustomerSuccessGeneral(
  payload: CustomerSuccessGeneralSettings,
): Promise<CustomerSuccessModuleSettings> {
  const response = await apiClient.put<CustomerSuccessModuleSettings>(
    '/v1/customer-success/settings/general',
    payload,
  );
  return normalizeModuleSettings(response.data);
}

export async function saveCustomerSuccessAttachments(
  payload: CustomerSuccessAttachmentSettings,
): Promise<CustomerSuccessModuleSettings> {
  const response = await apiClient.put<CustomerSuccessModuleSettings>(
    '/v1/customer-success/settings/attachments',
    payload,
  );
  return normalizeModuleSettings(response.data);
}

export async function saveCustomerSuccessAiSuggest(
  payload: CustomerSuccessAiSuggestSettingsUpsert,
): Promise<CustomerSuccessModuleSettings> {
  const response = await apiClient.put<CustomerSuccessModuleSettings>(
    '/v1/customer-success/settings/ai-suggest',
    payload,
  );
  return normalizeModuleSettings(response.data);
}
